<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.dashboard'),
                        href: meta.links.dashboard_index,
                    },
                    {
                        name: $tc('messages.payments_quotas', 2),
                        href: meta.links.payments_index,
                    },
                    {
                        name: $t('messages.debt'),
                        href: null,
                    },
                    {
                        name: meta.info.school_year_range,
                        href: null,
                    },
                ]"
            />
        </template>

        <div>{{ $t('messages.total') }}: {{ meta.info.total_difference }}</div>

        <form action="" @submit.prevent="submit">
            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.groups', 1) }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.students', 1) }}
                                    </th>

                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.paid') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.payments_quotas', 1) }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.difference') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.quantity') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.recipient') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody v-auto-animate>
                                <tr
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                    v-for="(student, index) in form.students"
                                    :key="student.id"
                                >
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900 text-right tabular-nums"
                                    >
                                        {{ student.group_name }}
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <div>{{ student.full_name }}</div>
                                        <BadgePill color="yellow" v-if="!student.active" class="ml-1">
                                            {{ $t('messages.inactive') }}
                                        </BadgePill>
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900 text-right tabular-nums"
                                    >
                                        {{ student.paid_display }}
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900 text-right tabular-nums"
                                    >
                                        {{ student.expected_display }}
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900 text-right tabular-nums"
                                    >
                                        <BadgePill :color="student.is_negative ? 'red' : 'green'" class="ml-1">
                                            {{ student.difference_display }}
                                        </BadgePill>
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <FormInput
                                            required
                                            id="difference"
                                            v-model="student.difference"
                                            name="difference"
                                            type="number"
                                            class="mt-1 w-full tabular-nums text-right"
                                            :class="{ 'opacity-50': student.recipient_id === '' }"
                                            step="0.01"
                                        />
                                        <FormError :value="form.errors.difference" class="mt-2" />
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <FormSelect
                                            v-model="student.recipient_id"
                                            :null-option-text="$t('messages.No')"
                                            :options="student.recipients"
                                            class="w-full"
                                            v-if="student.recipients.length"
                                        />
                                        <BadgePill v-else color="gray" class="ml-1"> ??? </BadgePill>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="flex justify-end mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import BadgePill from '@/components/BadgePill.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';

export default {
    components: {
        FormInput,
        FormLabel,
        FormError,
        SubmitFormButton,
        FormSelect,
        BadgePill,
        PanelPage,
    },
    layout: AdminLayout,
    name: 'Index',
    props: ['meta', 'students'],
    data() {
        return {
            form: this.$inertia.form({
                students: this.students,
            }),
        };
    },
    methods: {
        submit() {
            this.form
                .transform((data) => {
                    data.students = data.students.map((s) => {
                        return {
                            student_id: s.id,
                            recipient_id: s.recipient_id,
                            difference: s.difference,
                        };
                    });
                    return data;
                })
                .patch(this.meta.links.update, {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.form.students = this.students;
                    },
                });
        },
    },
};
</script>
